import { useCallback, useEffect, useMemo, useState } from "react";
import { Calculator, Loader2, Plus, Save, Send, Trash2 } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatCurrency } from "@/lib/i18n";

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  costPriceCents: number;
}

interface EstimateTier {
  id: string;
  name: string;
  items: LineItem[];
}

interface EstimateServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  priceCents: number | null;
  currency: string;
}

interface CreateEstimateResponse {
  data: {
    id: string;
    title: string;
    status: string;
    personId: string;
    customerEmail: string;
  };
}

interface SendEstimateResponse {
  data: {
    proposalId: string;
    reviewUrl: string;
    signatureRequestId: string;
  };
}

function createItem(): LineItem {
  return {
    id: crypto.randomUUID(),
    description: "",
    quantity: 1,
    unitPriceCents: 0,
    costPriceCents: 0,
  };
}

function createDefaultTiers(): EstimateTier[] {
  return [
    { id: "good", name: "Good", items: [createItem()] },
    { id: "better", name: "Better", items: [createItem()] },
    { id: "best", name: "Best", items: [createItem()] },
  ];
}

function tierTotal(items: LineItem[]) {
  return items.reduce((sum, item) => sum + item.quantity * item.unitPriceCents, 0);
}

function tierCost(items: LineItem[]) {
  return items.reduce((sum, item) => sum + item.quantity * item.costPriceCents, 0);
}

function tierMargin(items: LineItem[]) {
  const revenue = tierTotal(items);
  const cost = tierCost(items);
  if (revenue === 0) return 0;
  return Math.round(((revenue - cost) / revenue) * 100);
}

function toIsoValue(value: string) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export default function EstimateBuilderPage() {
  const [services, setServices] = useState<EstimateServiceOption[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savedProposalId, setSavedProposalId] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [desiredStartAt, setDesiredStartAt] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [tiers, setTiers] = useState<EstimateTier[]>(createDefaultTiers());

  useEffect(() => {
    let cancelled = false;
    setIsLoadingServices(true);
    apiFetch<{ data: EstimateServiceOption[] }>("/crm/bookings/estimates/services")
      .then((response) => {
        if (cancelled) return;
        setServices(response.data ?? []);
        if (!serviceId && response.data?.[0]?.id) {
          setServiceId(response.data[0].id);
        }
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Failed to load service options");
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingServices(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedService = useMemo(
    () => services.find((service) => service.id === serviceId) ?? null,
    [services, serviceId],
  );

  const updateItem = useCallback((tierIdx: number, itemId: string, field: keyof LineItem, value: string | number) => {
    setTiers((previous) => previous.map((tier, currentTierIdx) => (
      currentTierIdx === tierIdx
        ? {
          ...tier,
          items: tier.items.map((item) => (
            item.id === itemId
              ? { ...item, [field]: value }
              : item
          )),
        }
        : tier
    )));
  }, []);

  const addItem = useCallback((tierIdx: number) => {
    setTiers((previous) => previous.map((tier, currentTierIdx) => (
      currentTierIdx === tierIdx
        ? { ...tier, items: [...tier.items, createItem()] }
        : tier
    )));
  }, []);

  const removeItem = useCallback((tierIdx: number, itemId: string) => {
    setTiers((previous) => previous.map((tier, currentTierIdx) => (
      currentTierIdx === tierIdx
        ? { ...tier, items: tier.items.filter((item) => item.id !== itemId) }
        : tier
    )));
  }, []);

  const buildPayload = useCallback(() => {
    const desiredStartAtIso = toIsoValue(desiredStartAt);
    if (!customerName.trim()) throw new Error("Customer name is required.");
    if (!customerEmail.trim()) throw new Error("Customer email is required.");
    if (!serviceId) throw new Error("Choose a service.");
    if (!desiredStartAtIso) throw new Error("Choose a desired service time.");
    if (tiers.some((tier) => tier.items.some((item) => !item.description.trim()))) {
      throw new Error("Every line item needs a description.");
    }

    return {
      title: title.trim() || undefined,
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      serviceId,
      desiredStartAt: desiredStartAtIso,
      validUntil: toIsoValue(validUntil),
      tiers: tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        items: tier.items.map((item) => ({
          id: item.id,
          description: item.description.trim(),
          quantity: item.quantity,
          unitPriceCents: item.unitPriceCents,
          costPriceCents: item.costPriceCents,
        })),
      })),
    };
  }, [customerEmail, customerName, desiredStartAt, serviceId, tiers, title, validUntil]);

  const saveEstimate = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setReviewUrl(null);
    setIsSaving(true);
    try {
      const payload = buildPayload();
      const response = await apiFetch<CreateEstimateResponse>("/crm/bookings/estimates", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setSavedProposalId(response.data.id);
      setSuccess(`Saved draft estimate: ${response.data.title}`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Failed to save estimate");
      throw saveError;
    } finally {
      setIsSaving(false);
    }
  }, [buildPayload]);

  const handleSaveClick = useCallback(async () => {
    try {
      await saveEstimate();
    } catch {
      // handled in saveEstimate
    }
  }, [saveEstimate]);

  const handleSendClick = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setIsSending(true);
    try {
      const proposalId = savedProposalId ?? (await (async () => {
        const payload = buildPayload();
        const response = await apiFetch<CreateEstimateResponse>("/crm/bookings/estimates", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setSavedProposalId(response.data.id);
        return response.data.id;
      })());

      const response = await apiFetch<SendEstimateResponse>(`/crm/bookings/estimates/${proposalId}/send`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setReviewUrl(response.data.reviewUrl);
      setSuccess("Estimate sent to the client.");
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Failed to send estimate");
    } finally {
      setIsSending(false);
    }
  }, [buildPayload, savedProposalId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Estimate Builder</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Build live service estimates on the mounted booking runtime, then send the client a review-and-sign link.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleSaveClick()}
            disabled={isSaving || isSending || isLoadingServices}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => void handleSendClick()}
            disabled={isSaving || isSending || isLoadingServices}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send for Signature
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]">
          {success}
          {reviewUrl ? (
            <div className="mt-2 break-all text-xs text-[var(--color-foreground)]">
              Review link: <a className="underline" href={reviewUrl}>{reviewUrl}</a>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <div className="grid gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Estimate title</span>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Premium facial estimate"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Service</span>
              <select
                value={serviceId}
                onChange={(event) => setServiceId(event.target.value)}
                disabled={isLoadingServices}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              >
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Customer name</span>
              <input
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder="Alex Example"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Customer email</span>
              <input
                type="email"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
                placeholder="alex@example.com"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Desired start</span>
              <input
                type="datetime-local"
                value={desiredStartAt}
                onChange={(event) => setDesiredStartAt(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Valid until</span>
              <input
                type="datetime-local"
                value={validUntil}
                onChange={(event) => setValidUntil(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            {tiers.map((tier, tierIdx) => {
              const total = tierTotal(tier.items);
              const margin = tierMargin(tier.items);
              return (
                <section key={tier.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]">
                  <header className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
                    <div>
                      <h2 className="font-semibold text-[var(--color-foreground)]">{tier.name}</h2>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{tier.items.length} line items</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[var(--color-foreground)]">
                        {formatCurrency(total, selectedService?.currency ?? "EUR", { minimumFractionDigits: 2 })}
                      </div>
                      <div className="inline-flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
                        <Calculator className="h-3 w-3" />
                        {margin}% margin
                      </div>
                    </div>
                  </header>

                  <div className="space-y-3 p-4">
                    {tier.items.map((item) => (
                      <div key={item.id} className="space-y-2 rounded-lg bg-[var(--color-surface)] p-3">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(event) => updateItem(tierIdx, item.id, "description", event.target.value)}
                          placeholder="Discovery workshop"
                          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-sm"
                        />
                        <div className="grid grid-cols-[repeat(3,minmax(0,1fr))_32px] gap-2">
                          <label className="space-y-1 text-[10px] text-[var(--color-muted-foreground)]">
                            <span>Qty</span>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) => updateItem(tierIdx, item.id, "quantity", Math.max(1, Number.parseInt(event.target.value || "1", 10)))}
                              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-sm text-[var(--color-foreground)]"
                            />
                          </label>
                          <label className="space-y-1 text-[10px] text-[var(--color-muted-foreground)]">
                            <span>Price (cents)</span>
                            <input
                              type="number"
                              min="0"
                              value={item.unitPriceCents}
                              onChange={(event) => updateItem(tierIdx, item.id, "unitPriceCents", Math.max(0, Number.parseInt(event.target.value || "0", 10)))}
                              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-sm text-[var(--color-foreground)]"
                              aria-label={`${tier.name} sell price`}
                            />
                          </label>
                          <label className="space-y-1 text-[10px] text-[var(--color-muted-foreground)]">
                            <span>Cost (cents)</span>
                            <input
                              type="number"
                              min="0"
                              value={item.costPriceCents}
                              onChange={(event) => updateItem(tierIdx, item.id, "costPriceCents", Math.max(0, Number.parseInt(event.target.value || "0", 10)))}
                              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-sm text-[var(--color-foreground)]"
                              aria-label={`${tier.name} cost price`}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeItem(tierIdx, item.id)}
                            className="self-end rounded p-1 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                            aria-label={`Remove ${tier.name} line item`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addItem(tierIdx)}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Item
                    </button>
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        <aside className="space-y-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Mounted estimate summary</h2>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Selected service</dt>
              <dd className="font-medium text-[var(--color-foreground)]">{selectedService?.name ?? "Choose a service"}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Duration</dt>
              <dd className="font-medium text-[var(--color-foreground)]">
                {selectedService ? `${selectedService.durationMinutes} minutes` : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Recommended tier</dt>
              <dd className="font-medium text-[var(--color-foreground)]">
                {tiers[1] ? `${tiers[1].name} · ${formatCurrency(tierTotal(tiers[1].items), selectedService?.currency ?? "EUR", { minimumFractionDigits: 2 })}` : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Draft ID</dt>
              <dd className="font-medium text-[var(--color-foreground)]">{savedProposalId ?? "Not saved yet"}</dd>
            </div>
          </dl>

          <div className="rounded-lg bg-[var(--color-surface)] p-4 text-xs text-[var(--color-muted-foreground)]">
            Save writes the estimate into the canonical `proposals` + `proposal_sections` runtime. Send creates a signed review link over the mounted public estimate route and email flow.
          </div>
        </aside>
      </div>
    </div>
  );
}
